import { supabase } from './supabase';

/**
 * Storage utility for uploading banner and carousel images
 */

// Bucket names (use lowercase IDs that Supabase expects)
const BANNERS_BUCKET = 'banners';
const CAROUSEL_BUCKET = 'carousel';

/**
 * Upload a banner image
 */
export async function uploadBannerImage(file: File): Promise<string> {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  const fileName = `banner-${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
  
  // Check/create bucket
  const bucketReady = await ensureBucketExists(BANNERS_BUCKET);
  
  if (!bucketReady) {
    // Bucket doesn't exist and couldn't be created - try to upload anyway
    // (it might have been created manually in Supabase dashboard)
    console.warn(`[Storage] Bucket ${BANNERS_BUCKET} may not exist. Attempting upload anyway...`);
  }

  const { error } = await supabase.storage
    .from(BANNERS_BUCKET)
    .upload(fileName, file);

  if (error) {
    throw new Error(`Failed to upload banner: ${error.message}`);
  }

  const { data: { publicUrl } } = supabase.storage
    .from(BANNERS_BUCKET)
    .getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Upload a carousel image
 */
export async function uploadCarouselImage(file: File): Promise<string> {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  const fileName = `carousel-${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
  
  // Check/create bucket
  const bucketReady = await ensureBucketExists(CAROUSEL_BUCKET);
  
  if (!bucketReady) {
    // Bucket doesn't exist and couldn't be created - try to upload anyway
    console.warn(`[Storage] Bucket ${CAROUSEL_BUCKET} may not exist. Attempting upload anyway...`);
  }

  const { error } = await supabase.storage
    .from(CAROUSEL_BUCKET)
    .upload(fileName, file);

  if (error) {
    throw new Error(`Failed to upload carousel image: ${error.message}`);
  }

  const { data: { publicUrl } } = supabase.storage
    .from(CAROUSEL_BUCKET)
    .getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Ensure a bucket exists
 * Note: Due to RLS restrictions, buckets should be created manually in Supabase Dashboard
 * This function only checks if the bucket exists
 */
async function ensureBucketExists(bucketName: string): Promise<boolean> {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    const { data: buckets, error } = await supabase.storage.listBuckets();
    
    if (error) {
      console.error(`[Storage] Error listing buckets:`, error);
      return false;
    }

    const bucketExists = buckets?.some(b => b.name === bucketName);
    
    if (!bucketExists) {
      console.warn(`[Storage] Bucket '${bucketName}' does not exist.`);
      console.warn(`[Storage] Please create it in Supabase Dashboard > Storage > New bucket`);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error(`[Storage] Unexpected error in ensureBucketExists:`, err);
    return false;
  }
}

/**
 * Delete an image from storage
 */
export async function deleteStorageImage(bucketName: string, imageUrl: string): Promise<void> {
  if (!supabase) {
    return;
  }

  // Extract file path from URL
  const urlParts = imageUrl.split(`/storage/v1/object/public/${bucketName}/`);
  if (urlParts.length < 2) {
    return;
  }

  const filePath = urlParts[1];
  
  const { error } = await supabase.storage
    .from(bucketName)
    .remove([filePath]);

  if (error) {
    console.error(`Error deleting image:`, error);
  }
}